<template>
  <div class="homepage bg-white">
    <!-- Header & Hero Section -->
    <header class="header bg-gradient-to-br from-blue-600 to-indigo-800 text-white">
      <div class="w-full">
        <!-- Navigation Bar -->
        <nav class="container mx-auto px-6 py-4 flex justify-between items-center">
          <div class="logo text-3xl font-bold">
            <span class="handwriting-font">EasyMarks</span> AI
          </div>

          <!-- Desktop Navigation -->
          <ul class="hidden md:flex nav-links items-center space-x-6 text-sm">
            <li><a href="#features" class="hover:text-blue-200 transition-colors">Features</a></li>
            <li><a href="#how-it-works" class="hover:text-blue-200 transition-colors">How It Works</a></li>
            <li><a href="#contact" class="hover:text-blue-200 transition-colors">Contact</a></li>
            <li><button @click="goToLogin" class="login-btn">Login</button></li>
            <li><button @click="getStarted" class="get-started-btn">Get Started</button></li>
          </ul>

          <!-- Mobile Hamburger Menu -->
          <button class="md:hidden hamburger-menu" @click="toggleMenu" aria-label="Menu">
            <span class="bar" :class="{ open: isMenuActive }"></span>
            <span class="bar" :class="{ open: isMenuActive }"></span>
            <span class="bar" :class="{ open: isMenuActive }"></span>
          </button>
        </nav>
      </div>

      <!-- Mobile Menu Overlay -->
      <div v-if="isMenuActive" class="md:hidden mobile-menu">
        <ul class="flex flex-col items-center justify-center h-full space-y-8">
          <li @click="closeMenu"><a href="#features" class="text-2xl">Features</a></li>
          <li @click="closeMenu"><a href="#how-it-works" class="text-2xl">How It Works</a></li>
          <li @click="closeMenu"><a href="#contact" class="text-2xl">Contact</a></li>
          <li @click="goToLogin"><button class="login-btn mt-4">Login</button></li>
          <li @click="getStarted"><button class="get-started-btn">Get Started</button></li>
        </ul>
      </div>

      <!-- Hero Content -->
      <div class="hero container mx-auto px-6 text-center pt-24 pb-32">
        <h1 class="text-4xl md:text-6xl font-extrabold leading-tight mb-4">The AI Assistant That Grades For You</h1>
        <p class="text-lg md:text-xl text-blue-200 mb-8 max-w-3xl mx-auto">
          Stop spending nights grading papers. Scan handwritten tests and get accurate results in minutes.
          <br class="hidden md:block">
          <strong>Start for free with complimentary credits.</strong>
        </p>
        <button @click="getStarted" class="cta-btn">Claim Your Free Credits</button>
        <p class="mt-4 text-sm text-blue-300">No credit card required. Pay as you go.</p>
      </div>
    </header>

    <!-- Features Section -->
    <section id="features" class="features container mx-auto px-6 py-20">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
        <div class="feature-card">
          <div class="icon-wrapper bg-blue-100 text-blue-600">
            <svg class="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
          </div>
          <h2 class="text-2xl font-bold mt-6 mb-2">Flexible Input</h2>
          <p class="text-gray-600">Simply copy-paste your tests and rubrics, or upload them as images directly from your computer or phone.</p>
        </div>
        <div class="feature-card">
          <div class="icon-wrapper bg-green-100 text-green-600">
            <svg class="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
          </div>
          <h2 class="text-2xl font-bold mt-6 mb-2">AI-Powered Scanning</h2>
          <p class="text-gray-600">Our advanced OCR reads everything, from typed questions to handwritten student answers, with high accuracy.</p>
        </div>
        <div class="feature-card">
          <div class="icon-wrapper bg-indigo-100 text-indigo-600">
            <svg class="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </div>
          <h2 class="text-2xl font-bold mt-6 mb-2">Automated Grading</h2>
          <p class="text-gray-600">Let our AI apply your rubric, grade answers, calculate points, and provide justifications for you automatically.</p>
        </div>
      </div>
    </section>

    <!-- How It Works Section -->
    <section id="how-it-works" class="how-it-works bg-gray-50 py-20">
      <div class="container mx-auto px-6 text-center">
        <h2 class="text-3xl font-bold mb-12">Simple Steps to Effortless Grading</h2>
        <div class="max-w-4xl mx-auto space-y-8">
          <div class="step">
            <div class="step-number">1</div>
            <div class="step-content">
              <h3>Provide Your Test</h3>
              <p>Paste your questions and answer key, or simply upload pictures of the pages.</p>
            </div>
          </div>
          <div class="step">
            <div class="step-number">2</div>
            <div class="step-content">
              <h3>Scan Student Work</h3>
              <p>Use our integrated phone scanner to quickly upload handwritten student answer sheets.</p>
            </div>
          </div>
          <div class="step">
            <div class="step-number">3</div>
            <div class="step-content">
              <h3>Let AI Do the Work</h3>
              <p>Our AI processes everything, grading each answer based on your rubric and providing feedback.</p>
            </div>
          </div>
          <div class="step">
            <div class="step-number">4</div>
            <div class="step-content">
              <h3>Review & Export</h3>
              <p>Quickly review the graded tests, make any adjustments, and export the final results.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer id="contact" class="footer bg-gray-800 text-gray-300 py-12">
      <div class="container mx-auto px-6 text-center">
        <p class="font-bold text-white text-lg">EasyMarks.AI</p>
        <p class="my-4">Making grading easier for educators everywhere.</p>
        <p class="text-sm">
          <a href="mailto:support@easymarks.ai" class="hover:text-white">support@easymarks.ai</a>
        </p>
        <div class="mt-4">
          <router-link to="/terms-of-service" class="text-sm hover:text-white underline">Terms of Service</router-link>
        </div>
        <p class="text-xs text-gray-500 mt-8">&copy; 2024 EasyMarks.AI. All rights reserved.</p>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'

const isMenuActive = ref(false)
const router = useRouter()

watch(isMenuActive, (newVal) => {
  if (newVal) {
    document.body.classList.add('no-scroll')
  } else {
    document.body.classList.remove('no-scroll')
  }
})

const getStarted = () => {
  isMenuActive.value = false
  router.push('/signup')
}

const goToLogin = () => {
  isMenuActive.value = false
  router.push('/login')
}

const toggleMenu = () => {
  isMenuActive.value = !isMenuActive.value
}

const closeMenu = () => {
  isMenuActive.value = false
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Indie+Flower&display=swap');

.no-scroll {
  overflow: hidden;
}

.handwritten {
  font-family: 'Indie Flower', cursive;
  @apply text-blue-300 font-normal;
}

.logo .handwriting-font {
  font-family: 'Indie Flower', cursive;
}

.login-btn {
  @apply bg-transparent border-2 border-white px-4 py-2 rounded-md hover:bg-white hover:text-blue-600 transition-colors;
}

.get-started-btn {
  @apply bg-white text-blue-600 font-bold px-5 py-2 rounded-md hover:bg-blue-100 transition-colors;
}

.cta-btn {
  @apply bg-white text-blue-600 font-bold text-lg px-8 py-4 rounded-lg shadow-lg hover:bg-blue-100 transition transform hover:scale-105;
}

.icon-wrapper {
  @apply inline-flex items-center justify-center h-20 w-20 rounded-full;
}

.step {
  @apply flex items-start text-left space-x-4;
}

.step-number {
  @apply flex-shrink-0 h-12 w-12 flex items-center justify-center rounded-full bg-blue-600 text-white font-bold text-xl;
}

.step-content h3 {
  @apply text-xl font-semibold text-gray-800;
}

.step-content p {
  @apply text-gray-600;
}

.hamburger-menu .bar {
  display: block;
  width: 28px;
  height: 3px;
  margin: 5px auto;
  background-color: #fff;
  transition: all 0.3s ease-in-out;
}

.hamburger-menu .bar.open:nth-child(1) {
  transform: translateY(8px) rotate(45deg);
}

.hamburger-menu .bar.open:nth-child(2) {
  opacity: 0;
}

.hamburger-menu .bar.open:nth-child(3) {
  transform: translateY(-8px) rotate(-45deg);
}

.mobile-menu {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(17, 24, 39, 0.95);
  z-index: 50;
}
</style>
